import React, { useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert, Autocomplete, Box, Button, Checkbox, CircularProgress, Link, Paper, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography
} from '@mui/material';

const api = `${process.env.REACT_APP_API_URL}/api/admin/friendlies`;
const headers = { 'Content-Type': 'application/json', 'X-API-Key': process.env.REACT_APP_MAKE_API_KEY };

export default function FriendliesManager() {
  const [since, setSince] = useState('2026-06-01');
  const [matches, setMatches] = useState([]);
  const [selected, setSelected] = useState({});
  const [assignments, setAssignments] = useState({});
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [warnings, setWarnings] = useState([]);

  const readySelected = useMemo(() => matches.filter((m) => selected[m.sourceId] &&
    (assignments[`${m.sourceId}-home`] || m.homeTeam) && (assignments[`${m.sourceId}-away`] || m.awayTeam)),
  [matches, selected, assignments]);

  const fetchMatches = async () => {
    setLoading(true); setError(''); setMessage('');
    try {
      const { data } = await axios.post(`${api}/fetch`, { since }, { headers });
      setMatches(data.matches || []); setWarnings(data.warnings || []);
      setSelected(Object.fromEntries((data.matches || []).map((m) => [m.sourceId, Boolean(m.homeTeam && m.awayTeam)])));
      setAssignments({});
      setMessage(`Pobrano ${data.matches?.length || 0} nowych sparingów.`);
    } catch (e) { setError(e.response?.data?.error || e.message); }
    finally { setLoading(false); }
  };

  const searchTeams = async (value) => {
    if (!value || value.length < 2) return;
    try { const { data } = await axios.get(`${api}/teams`, { params: { q: value }, headers }); setOptions(data); } catch (_) {}
  };

  const payload = (m) => ({ sourceId: m.sourceId, sourceUrl: m.sourceUrl, matchDate: m.matchDate,
    homeName: m.homeName, awayName: m.awayName, homeGoals: m.homeGoals, awayGoals: m.awayGoals });
  const team = (m, side) => assignments[`${m.sourceId}-${side}`] || m[`${side}Team`];

  const calculateEloChange = (homeElo, awayElo, homeGoals, awayGoals) => {
    const goalDifference = Math.abs(homeGoals - awayGoals);
    let goalMultiplier = 1;
    if (goalDifference === 2) goalMultiplier = 1.5;
    if (goalDifference > 2) goalMultiplier = (11 + goalDifference) / 8;
    const expectedHome = 1 / (1 + Math.pow(10, -(homeElo - awayElo) / 400));
    const scoreHome = homeGoals === awayGoals ? 0.5 : homeGoals > awayGoals ? 1 : 0;
    return Math.round(10 * (scoreHome - expectedHome) * goalMultiplier);
  };

  const eloPreview = (m) => {
    const home = team(m, 'home');
    const away = team(m, 'away');
    if (!home || !away) return 'Przypisz obie drużyny';
    const change = calculateEloChange(home.elo, away.elo, m.homeGoals, m.awayGoals);
    return `${home.elo} / ${away.elo} → ${change > 0 ? '+' : ''}${change}`;
  };

  const acceptOne = async (m) => {
    const home = team(m, 'home'); const away = team(m, 'away');
    if (!home || !away) { setError('Przypisz obie nierozpoznane drużyny.'); return; }
    setBusyId(m.sourceId); setError('');
    try {
      const { data } = await axios.post(`${api}/accept`, { ...payload(m), homeTeamId: home.id, awayTeamId: away.id }, { headers });
      setMatches((old) => old.filter((x) => x.sourceId !== m.sourceId));
      setMessage(`Zaakceptowano ${m.homeName} – ${m.awayName}; zmiana Elo gospodarza: ${data.eloChange > 0 ? '+' : ''}${data.eloChange}.`);
    } catch (e) { setError(e.response?.data?.error || e.message); }
    finally { setBusyId(null); }
  };

  const acceptSelected = async () => {
    for (const m of readySelected) { // kolejność chronologiczna: każda odpowiedź uwzględnia poprzednią zmianę Elo
      // eslint-disable-next-line no-await-in-loop
      await acceptOne(m);
    }
  };

  const reject = async (m) => {
    setBusyId(m.sourceId); setError('');
    try {
      await axios.post(`${api}/reject`, { ...payload(m), reason: 'Odrzucono przez administratora' }, { headers });
      setMatches((old) => old.filter((x) => x.sourceId !== m.sourceId));
      setMessage('Sparing odrzucony. Nie pojawi się przy kolejnym pobraniu.');
    } catch (e) { setError(e.response?.data?.error || e.message); }
    finally { setBusyId(null); }
  };

  const picker = (m, side) => {
    const found = m[`${side}Team`];
    if (found) return <Box>{found.name}<Typography variant="caption" display="block">Elo {found.elo}</Typography></Box>;
    return <Autocomplete size="small" options={options} getOptionLabel={(o) => `${o.name} (${o.elo})`}
      value={assignments[`${m.sourceId}-${side}`] || null}
      onInputChange={(_, v) => searchTeams(v)} onChange={(_, v) => setAssignments((a) => ({ ...a, [`${m.sourceId}-${side}`]: v }))}
      renderInput={(params) => <TextField {...params} label={`Przypisz: ${m[`${side}Name`]}`} />} sx={{ minWidth: 230 }} />;
  };

  return <Box sx={{ p: { xs: 1, md: 3 } }}>
    <Typography variant="h4" gutterBottom>Import sparingów</Typography>
    <Typography color="text.secondary" sx={{ mb: 2 }}>Źródło: 90minut.pl · sparingi są neutralne · K=10 · rozgrywki #0</Typography>
    <Paper sx={{ p: 2, mb: 2 }}><Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
      <TextField type="date" label="Pobierz od daty" value={since} onChange={(e) => setSince(e.target.value)} InputLabelProps={{ shrink: true }} />
      <Button variant="contained" onClick={fetchMatches} disabled={loading}>{loading ? <CircularProgress size={22} /> : 'Pobierz sparingi'}</Button>
      <Button variant="outlined" onClick={acceptSelected} disabled={!readySelected.length || busyId !== null}>Akceptuj zaznaczone ({readySelected.length})</Button>
    </Stack></Paper>
    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
    {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
    {warnings.length > 0 && <Alert severity="warning" sx={{ mb: 2 }}>{warnings.join(' · ')}</Alert>}
    <TableContainer component={Paper}><Table size="small"><TableHead><TableRow>
      <TableCell padding="checkbox"><Checkbox checked={matches.length > 0 && matches.every((m) => selected[m.sourceId])}
        onChange={(e) => setSelected(Object.fromEntries(matches.map((m) => [m.sourceId, e.target.checked])))} /></TableCell>
      <TableCell>Data / wynik</TableCell><TableCell>Gospodarz</TableCell><TableCell>Gość</TableCell><TableCell>Elo</TableCell><TableCell>Akcje</TableCell>
    </TableRow></TableHead><TableBody>{matches.map((m) => <TableRow key={m.sourceId} selected={selected[m.sourceId]}>
      <TableCell padding="checkbox"><Checkbox checked={Boolean(selected[m.sourceId])} onChange={(e) => setSelected((s) => ({ ...s, [m.sourceId]: e.target.checked }))} /></TableCell>
      <TableCell><Link href={m.sourceUrl} target="_blank" rel="noreferrer">{m.matchDate.replace('T', ' ')}</Link><Typography fontWeight="bold">{m.homeGoals}:{m.awayGoals}</Typography></TableCell>
      <TableCell>{picker(m, 'home')}</TableCell><TableCell>{picker(m, 'away')}</TableCell>
      <TableCell>{eloPreview(m)}</TableCell>
      <TableCell><Stack spacing={1}><Button size="small" variant="contained" disabled={busyId !== null || !team(m, 'home') || !team(m, 'away')} onClick={() => acceptOne(m)}>Akceptuj</Button>
        <Button size="small" color="error" disabled={busyId !== null} onClick={() => reject(m)}>Odrzuć</Button></Stack></TableCell>
    </TableRow>)}</TableBody></Table></TableContainer>
  </Box>;
}
